/**
 * The extension rule (1.13.0, Teampace 2986/2995; Joey, Nov 2: "All extensions should be a minimum
 * of 3 digits. It is important however that we do not mix and match… we should always follow the
 * format the customer has started with.").
 *
 * - A new extension (a person, voicemail box, ring group, call queue or auto attendant) is 3 to 6
 *   digits.
 * - Once a company has people, every new extension has as many digits as theirs do (the most
 *   common length among them; a tie goes to the longer). Before it has people, its ring groups,
 *   queues and menus set it; before anything, the first extension made does.
 * - Stitch's own hidden numbers (Patch rings and the like) never count.
 *
 * Shared by the planner (which decides) and the web app (which says so as you type).
 */

export const EXTENSION_MIN = 3;
export const EXTENSION_MAX = 6;

/** The kinds a new extension is made for: the rule applies to these. */
export const NUMBERED_KINDS = ['person', 'ringGroup', 'callQueue', 'autoAttendant'] as const;

const digitsOnly = (e: string) => new RegExp(`^\\d{${EXTENSION_MIN},${EXTENSION_MAX}}$`).test(e);

/** The most common length among these extensions (a tie: the longer), or null. */
function commonLength(list: readonly string[]): { length: number; example: string } | null {
  const counts = new Map<number, string[]>();
  for (const e of list) {
    if (!digitsOnly(e)) continue;
    counts.set(e.length, [...(counts.get(e.length) ?? []), e]);
  }
  let best: { length: number; example: string; n: number } | null = null;
  for (const [length, xs] of counts) {
    if (!best || xs.length > best.n || (xs.length === best.n && length > best.length)) best = { length, example: [...xs].sort()[0]!, n: xs.length };
  }
  return best ? { length: best.length, example: best.example } : null;
}

/**
 * How many digits a company's new extensions have: from its people, else its ring groups, queues
 * and menus, else null (any 3 to 6, and the first one made decides).
 */
export function companyExtensionLength(people: readonly string[], others: readonly string[] = []): { length: number; example: string } | null {
  return commonLength(people) ?? commonLength(others);
}

/** Why a new extension doesn't fit (null: it does). */
export function extensionProblem(extension: string, company: { length: number; example: string } | null): string | null {
  const e = extension.trim();
  if (!/^\d+$/.test(e)) return `Use digits only, ${EXTENSION_MIN} to ${EXTENSION_MAX} of them.`;
  if (e.length < EXTENSION_MIN || e.length > EXTENSION_MAX) return `Use ${EXTENSION_MIN} to ${EXTENSION_MAX} digits.`;
  if (company && e.length !== company.length) return `Your extensions have ${company.length} digits (like ${company.example}). Use ${company.length} digits here too.`;
  return null;
}

/** The hint under an extension field: what fits at this company. */
export function extensionHint(company: { length: number; example: string } | null): string {
  return company ? `${company.length} digits, like your others (${company.example}).` : `${EXTENSION_MIN} to ${EXTENSION_MAX} digits. Your later extensions will have as many digits as this one.`;
}

/** The company's extension length from its directory (people first, then ring groups, queues and menus). */
export function companyLengthFrom(entries: readonly { extension: string; kind: string }[]): { length: number; example: string } | null {
  const of = (kinds: readonly string[]) => entries.filter((e) => kinds.includes(e.kind)).map((e) => e.extension);
  return companyExtensionLength(of(['person']), of(['ringGroup', 'callQueue', 'autoAttendant']));
}

/** Whether the rule applies to a new account of this kind. */
export const isNumberedKind = (kind: string): boolean => (NUMBERED_KINDS as readonly string[]).includes(kind);
